<template>
  <div class="photo-gallery">
    <b-overlay :show="isFetching" rounded="sm" spinner-variant="primary">
      <div class="gallery-toolbar">
        <h2 class="gallery-title">รูป</h2>
        <span class="gallery-count" :aria-label="`${photos.length} ไฟล์`">{{ photos.length }}</span>
        <span class="gallery-spacer"></span>
        <div class="gallery-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model.trim="search"
            type="search"
            class="form-control"
            placeholder="ค้นหาจากคำอธิบาย"
            aria-label="ค้นหาจากคำอธิบาย"
          />
        </div>
        <div class="gallery-segment" role="group" aria-label="จัดกลุ่ม">
          <button
            type="button"
            :aria-pressed="groupBy === 'category' ? 'true' : 'false'"
            :class="{ on: groupBy === 'category' }"
            @click="groupBy = 'category'"
          >
            <i class="fas fa-th-large" aria-hidden="true"></i> ตามประเภท
          </button>
          <button
            type="button"
            :aria-pressed="groupBy === 'treatment' ? 'true' : 'false'"
            :class="{ on: groupBy === 'treatment' }"
            @click="groupBy = 'treatment'"
          >
            <i class="far fa-calendar" aria-hidden="true"></i> ตามวันที่รักษา
          </button>
        </div>
        <b-button variant="primary" class="gallery-upload" @click="showUploadModal(activeCategory)">
          <i class="fas fa-upload" aria-hidden="true"></i> อัปโหลด
        </b-button>
      </div>

      <div class="gallery-chips" role="group" aria-label="กรองประเภท">
        <button
          v-for="chip in chips"
          :key="chip.value"
          type="button"
          class="gallery-chip"
          :class="{ on: filter === chip.value }"
          :aria-pressed="filter === chip.value ? 'true' : 'false'"
          @click="filter = chip.value"
        >
          <span v-if="chip.color" class="chip-dot" :style="{ background: chip.color }"></span>
          {{ chip.text }}
          <span class="chip-count">{{ chip.count }}</span>
        </button>
      </div>

      <template v-if="groupBy === 'category'">
        <CategoryCard
          v-for="category in shownCategories"
          :key="category.value"
          :ref="`card-${category.value}`"
          :title="category.text"
          :category="category.value"
          :items="itemsOf(category.value)"
          :selected-files="selectedFiles"
          :patient-id="patient.id"
          :active="activeCategory === category.value"
          @activate="activeCategory = $event"
          @upload="showUploadModal"
          @upload-success="handleUploadSuccess"
          @upload-error="handleUploadError"
          @select="toggleFileSelection"
          @select-all="selectAllIn"
          @preview="handlePreview"
          @download="handleDownload"
          @delete="handleDelete"
          @sign="openSignTab"
          @radiiView="openRadiiView"
        />
      </template>
      <template v-else>
        <CategoryCard
          v-for="group in treatmentGroups"
          :key="group.key"
          :title="group.title"
          :items="group.items"
          :selected-files="selectedFiles"
          :allow-upload="false"
          @select="toggleFileSelection"
          @preview="handlePreview"
          @download="handleDownload"
          @delete="handleDelete"
          @sign="openSignTab"
          @radiiView="openRadiiView"
        />
        <p v-if="treatmentGroups.length === 0" class="gallery-empty">ไม่พบรูป</p>
      </template>
    </b-overlay>

    <GalleryActions
      :selected-files="selectedFiles"
      :select-all-label="selectAllLabel"
      :notice="downloadNotice"
      :can-share="canShareFiles"
      :share-ready="!!preparedShare"
      unit="รูป"
      @select-all="selectAllIn(selectionCategory)"
      @clear="clearSelection"
      @bulk-download="handleBulkDownload"
      @bulk-delete="handleBulkDelete"
      @share="handleShare"
    />
    <MobileUploadBar
      v-if="selectedFiles.length === 0"
      @pick="showUploadModal(activeCategory)"
      @captured="onCaptured"
    />

    <Lightbox
      :index="lightBoxIndex"
      :items="lightBoxList"
      :toolbar="[
        'counter',
        'zoom',
        'slide',
        'rotate',
        'gallery',
        'fullscreen',
        'close',
      ]"
      @close="resetLightbox"
    />
    <UploadFilesModal
      :patientId="patient.id"
      :patientDn="patient.dn"
      ref="uploadFiles"
      @uploadedFiles="onFilesUploaded"
    />
    <Loading v-if="isLoading" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import GalleryMixin from "@/mixins/GalleryMixin";
import VueLightbox from "vue-lightbox-lite";
import CategoryCard from "./CategoryCard.vue";
import GalleryActions from "./GalleryActions.vue";
import MobileUploadBar from "./MobileUploadBar.vue";
import UploadFilesModal from "@/components/modal/UploadFiles.vue";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading.vue";
import "vue-lightbox-lite/dist/index.css";
import formatDate from "@/utils/formatDate";
import { categoryTheme } from "@/utils/categoryTheme";
import { pasteBelongsHere } from "@/utils/pasteTarget";

const PHOTO_CATEGORIES = [
  { text: "Intra-Oral", value: "IntraOralPhoto" },
  { text: "Extra-Oral", value: "ExtraOralPhoto" },
  { text: "X-Ray", value: "XRay" },
  { text: "OPD Card", value: "OpdCard" },
];

export default {
  name: "PhotoGallery",
  mixins: [GalleryMixin],
  components: {
    Lightbox: VueLightbox,
    CategoryCard,
    GalleryActions,
    MobileUploadBar,
    UploadFilesModal,
    Dialog,
    Loading,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      filter: "all",
      search: "",
      groupBy: "category",
      // the section that takes Ctrl+V
      activeCategory: null,
    };
  },
  computed: {
    zipLabel() {
      return "รูป";
    },
    photos() {
      const values = PHOTO_CATEGORIES.map(c => c.value);
      return this.getFileList.filter(f => values.includes(f.category));
    },
    matchingPhotos() {
      const query = this.search.toLowerCase();
      return this.photos.filter(
        f =>
          (this.filter === "all" || f.category === this.filter) &&
          (!query || (f.remark || "").toLowerCase().includes(query))
      );
    },
    chips() {
      return [
        { value: "all", text: "ทั้งหมด", count: this.photos.length },
        ...PHOTO_CATEGORIES.map(c => ({
          value: c.value,
          text: c.text,
          color: categoryTheme(c.value).color,
          count: this.photos.filter(f => f.category === c.value).length,
        })),
      ];
    },
    shownCategories() {
      return this.filter === "all"
        ? PHOTO_CATEGORIES
        : PHOTO_CATEGORIES.filter(c => c.value === this.filter);
    },
    // Newest treatment first; files with no treatment last.
    treatmentGroups() {
      const groups = new Map();
      this.matchingPhotos.forEach(file => {
        const date = file.treatmentId ? this.treatmentDates.get(file.treatmentId) : null;
        const key = date ? String(file.treatmentId) : "none";
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            date,
            title: date ? `รักษา ${formatDate(date)}` : "ไม่ระบุวันที่รักษา",
            items: [],
          });
        }
        groups.get(key).items.push(file);
      });
      return [...groups.values()].sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });
    },
    selectAllLabel() {
      const category = this.selectionCategory;
      if (!category) return null;
      const total = this.photos.filter(f => f.category === category).length;
      if (total <= this.selectedFiles.length) return null;
      const name = (PHOTO_CATEGORIES.find(c => c.value === category) || {}).text;
      return `เลือกทั้งหมดใน ${name} (${total})`;
    },
  },
  mounted() {
    document.addEventListener("paste", this.onPaste);
  },
  beforeDestroy() {
    document.removeEventListener("paste", this.onPaste);
  },
  methods: {
    // A photo taken from the bottom bar goes to the upload sheet, filed under
    // the section in use (Intra-Oral when none is).
    onCaptured(files) {
      this.$refs.uploadFiles.show({
        category: this.activeCategory || "IntraOralPhoto",
        files,
      });
    },
    itemsOf(category) {
      return this.matchingPhotos.filter(f => f.category === category);
    },
    // Ctrl+V goes to the section last clicked. The upload dialog takes it
    // while it is open.
    onPaste(event) {
      const modal = this.$refs.uploadFiles;
      if (modal && modal.isOpen) return;
      if (!pasteBelongsHere(event.target, this.$el)) return;
      const files = Array.from((event.clipboardData && event.clipboardData.files) || []);
      if (files.length === 0) return;
      event.preventDefault();
      if (!this.activeCategory) {
        this.handleUploadError({ message: "คลิกที่หมวดที่ต้องการก่อน แล้วกด Ctrl + V อีกครั้ง" });
        return;
      }
      this.groupBy = "category";
      if (this.filter !== "all" && this.filter !== this.activeCategory) this.filter = "all";
      this.$nextTick(() => {
        const [card] = this.$refs[`card-${this.activeCategory}`] || [];
        if (card) card.receiveFiles(files, "วางจากคลิปบอร์ด");
      });
    },
  },
};
</script>

<style scoped>
/* พื้นเทาอ่อนให้การ์ดแต่ละหมวดลอยเด่น */
.photo-gallery {
  padding: 1.25rem 1.25rem 5rem;
  border-radius: 18px;
  background: #f4f5f7;
  color: #101828;
}
.gallery-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.gallery-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.gallery-count {
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  background: #e7eaf0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3d4557;
}
.gallery-spacer {
  flex: 1 1 auto;
}
.gallery-search {
  position: relative;
  width: 260px;
}
.gallery-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #5d6679;
}
.gallery-search .form-control {
  height: 42px;
  padding-left: 2.5rem;
  border-radius: 999px;
  border-color: #d7dbe3;
}
.gallery-segment {
  display: flex;
  gap: 2px;
  padding: 4px;
  border-radius: 999px;
  background: #e7eaf0;
}
.gallery-segment button {
  height: 34px;
  padding: 0 0.875rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #3d4557;
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
.gallery-segment button.on {
  background: #fff;
  color: #101828;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
}
.gallery-upload {
  height: 42px;
  padding: 0 1.125rem;
  border-radius: 999px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(11, 99, 197, 0.25);
}
.gallery-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.gallery-chip {
  height: 36px;
  padding: 0 0.5rem 0 0.875rem;
  border-radius: 999px;
  border: 1px solid #d7dbe3;
  background: #fff;
  color: #101828;
  font-size: 0.875rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.gallery-chip:hover {
  border-color: #9aa2b1;
}
.gallery-chip.on {
  background: #101828;
  border-color: #101828;
  color: #fff;
}
.chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 4px;
}
.chip-count {
  min-width: 22px;
  height: 22px;
  padding: 0 0.375rem;
  border-radius: 999px;
  background: #f1f3f6;
  color: #3d4557;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.gallery-chip.on .chip-count {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}
.gallery-empty {
  padding: 2rem 0;
  text-align: center;
  color: #5d6679;
}

@media (max-width: 575.98px) {
  .photo-gallery {
    padding: 0.875rem 0.875rem 0;
    margin: 0 -0.5rem;
    border-radius: 16px;
  }
  .gallery-toolbar {
    gap: 0.5rem;
  }
  .gallery-title {
    font-size: 1.5rem;
  }
  .gallery-upload {
    display: none;
  }
  .gallery-search {
    order: 5;
    width: 100%;
  }
  .gallery-search .form-control {
    font-size: 16px;
  }
  .gallery-segment {
    order: 6;
    width: 100%;
  }
  .gallery-segment button {
    flex: 1 1 0;
    justify-content: center;
  }
  .gallery-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    margin: 0 -0.875rem 0.75rem;
    padding: 0 0.875rem;
    scrollbar-width: none;
  }
  .gallery-chip {
    flex-shrink: 0;
  }
}
</style>
